import React from 'react';
import PortalTable, { Column } from '../../components/PortalTable/PortalTable';
import StatusBadge, { BadgeVariant } from '../../components/StatusBadge/StatusBadge';
import TrackingCell from '../../components/TrackingCell/TrackingCell';
import { RescueNanoShipmentGql, RescueOrderShipmentStatus } from '../../lib/rescueQueries';
import { formatDate } from '../../lib/utils';
import shipmentStyles from '../../styles/shipmentTable.module.scss';

const columns: Column[] = [
  { key: 'orderNumber', label: 'Order #' },
  { key: 'contents', label: 'Contents' },
  { key: 'orderDate', label: 'Ordered' },
  { key: 'status', label: 'Status' },
  { key: 'tracking', label: 'Tracking' },
];

const CHIPS_PER_BOX = 20;

// Only render box count when chipCount is a clean multiple of CHIPS_PER_BOX —
// the GraphQL response doesn't distinguish boxes from singles, so any remainder
// makes the box count ambiguous.
function formatContents(chipCount: number): string {
  const chipLabel = chipCount === 1 ? 'chip' : 'chips';
  if (chipCount === 0 || chipCount % CHIPS_PER_BOX !== 0) {
    return `${chipCount} ${chipLabel}`;
  }
  const boxes = chipCount / CHIPS_PER_BOX;
  return `${chipCount} ${chipLabel} (${boxes} ${boxes === 1 ? 'box' : 'boxes'})`;
}

type VisibleStatus = Exclude<RescueOrderShipmentStatus, 'CANCELED'>;

export type VisibleNanoShipment = RescueNanoShipmentGql & { shipmentStatus: VisibleStatus };

export function isVisibleNanoShipment(shipment: RescueNanoShipmentGql): shipment is VisibleNanoShipment {
  return shipment.shipmentStatus !== 'CANCELED';
}

const STATUS_LABELS: Record<VisibleStatus, string> = {
  ORDERED: 'Order Received',
  SHIPPED: 'In Transit',
};

const STATUS_VARIANT: Record<VisibleStatus, BadgeVariant> = {
  ORDERED: 'warning',
  SHIPPED: 'info',
};

interface OrdersTableProps {
  shipments: VisibleNanoShipment[];
}

export default function OrdersTable({ shipments }: OrdersTableProps) {
  return (
    <PortalTable columns={columns} isEmpty={shipments.length === 0} emptyMessage="No orders in progress">
      {shipments.map((shipment, idx) => {
        const [primaryOrder, ...additional] = shipment.orderNumbers;
        return (
          <tr key={`${primaryOrder ?? 'shipment'}-${idx}`}>
            <td>
              <div>{primaryOrder ?? '\u2014'}</div>
              {additional.length > 0 && <div className={shipmentStyles.orderNumbersList}>+{additional.join(', ')}</div>}
            </td>
            <td>{formatContents(shipment.chipCount)}</td>
            <td>{formatDate(shipment.orderDate)}</td>
            <td>
              <StatusBadge variant={STATUS_VARIANT[shipment.shipmentStatus]}>
                <span className={shipmentStyles.statusBadgeDot} />
                {STATUS_LABELS[shipment.shipmentStatus]}
              </StatusBadge>
            </td>
            <td>
              <TrackingCell trackingNumber={shipment.trackingNumber} />
            </td>
          </tr>
        );
      })}
    </PortalTable>
  );
}
